import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { useAskGraspd } from '../hooks/useAskGraspd';
import './AskGraspdPopup.css';

// SVG Icons for question types
const Icons = {
  sparkle: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707" />
      <circle cx="12" cy="12" r="4" />
    </svg>
  ),
  lightbulb: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 18h6M10 22h4M12 2a7 7 0 0 0-4 12.7V17a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-2.3A7 7 0 0 0 12 2z" />
    </svg>
  ),
  document: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  ),
  search: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
      <line x1="11" y1="8" x2="11" y2="14" />
      <line x1="8" y1="11" x2="14" y2="11" />
    </svg>
  ),
  question: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
  gear: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  ),
};

const QUESTION_TYPES = [
  { type: 'EXPLAIN', label: 'Explain', icon: Icons.lightbulb },
  { type: 'EXAMPLE', label: 'Example', icon: Icons.document },
  { type: 'DEEPER', label: 'Deeper', icon: Icons.search },
  { type: 'WHY', label: 'Why?', icon: Icons.question },
  { type: 'HOW', label: 'How?', icon: Icons.gear },
];

const QUESTION_LABELS = {
  EXPLAIN: 'Explain this in simpler terms',
  EXAMPLE: 'Give me a concrete example',
  DEEPER: 'Go deeper into this concept',
  WHY: 'Why is this important?',
  HOW: 'How does this work in practice?',
  CUSTOM: 'Custom question',
};

/**
 * @param {{
 *   selection: { text: string, startOffset: number, endOffset: number, position: { top: number, left: number } },
 *   courseId: string,
 *   lessonId: string,
 *   sectionId: string,
 *   onClose: () => void,
 *   onSuccess?: (item: { id: string, selectedText: string, startOffset: number, endOffset: number, questionType: string, customQuestion?: string, answer: string, createdAt: string }) => void
 * }} props
 */
export function AskGraspdPopup({ selection, courseId, lessonId, sectionId, onClose, onSuccess }) {
  const popupRef = useRef(null);
  const textareaRef = useRef(null);
  const [customQuestion, setCustomQuestion] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0, placement: 'below' });

  const {
    response,
    error,
    questionType,
    isInitial,
    isExpanded,
    isLoading,
    isCustom,
    showResponse,
    expand,
    showCustomInput,
    askQuestion,
    reset,
    goBack,
  } = useAskGraspd({
    courseId,
    lessonId,
    sectionId,
    selectedText: selection.text,
    startOffset: selection.startOffset,
    endOffset: selection.endOffset,
    onSuccess,
  });

  // Check for mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Calculate smart popup position
  useEffect(() => {
    if (isMobile || !selection.position) return;

    const calculatePosition = () => {
      const { selectionTop, selectionBottom, left } = selection.position;
      const gap = 8; // Gap between selection and popup
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const padding = 16; // Min distance from viewport edges

      // Get popup dimensions (use estimates if not yet rendered)
      const popupEl = popupRef.current;
      const popupHeight = popupEl?.offsetHeight || 200;
      const popupWidth = popupEl?.offsetWidth || 300;

      // Calculate space available above and below the selection
      const spaceBelow = viewportHeight - selectionBottom - gap;
      const spaceAbove = selectionTop - gap;

      // Decide placement: prefer below, flip above if not enough space
      let placement = 'below';
      let top;

      if (spaceBelow >= popupHeight || spaceBelow >= spaceAbove) {
        // Place below - either fits or has more space than above
        placement = 'below';
        top = selectionBottom + gap;
        // Clamp to viewport if it would overflow
        if (top + popupHeight > viewportHeight - padding) {
          top = viewportHeight - popupHeight - padding;
        }
      } else {
        // Place above
        placement = 'above';
        top = selectionTop - gap - popupHeight;
        // Clamp to viewport if it would overflow
        if (top < padding) {
          top = padding;
        }
      }

      // Calculate horizontal position (centered on selection, clamped to viewport)
      const halfWidth = popupWidth / 2;
      let finalLeft = left;

      // Clamp to viewport bounds
      if (finalLeft - halfWidth < padding) {
        finalLeft = halfWidth + padding;
      } else if (finalLeft + halfWidth > viewportWidth - padding) {
        finalLeft = viewportWidth - halfWidth - padding;
      }

      setPopupPosition({ top, left: finalLeft, placement });
    };

    // Calculate immediately
    calculatePosition();

    // Recalculate after a frame (once popup is rendered and we know its size)
    const rafId = requestAnimationFrame(calculatePosition);

    // Recalculate on resize
    window.addEventListener('resize', calculatePosition);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', calculatePosition);
    };
  }, [selection.position, isMobile, isInitial, isExpanded, isCustom, isLoading, showResponse]);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        onClose();
      }
    };

    // Small delay to avoid immediate close from the selection event
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  // Handle escape key to close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Focus textarea when entering custom input mode
  useEffect(() => {
    if (isCustom && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isCustom]);

  const handleCustomSubmit = useCallback(() => {
    if (customQuestion.trim().length >= 5) {
      askQuestion('CUSTOM', customQuestion.trim());
    }
  }, [askQuestion, customQuestion]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleCustomSubmit();
    }
  }, [handleCustomSubmit]);

  // Calculate popup position
  const getPopupStyle = () => {
    if (isMobile) {
      // Bottom sheet on mobile
      return {
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        borderRadius: 'var(--radius-panel) var(--radius-panel) 0 0',
        maxHeight: '80vh',
      };
    }

    // Floating popup on desktop - use smart positioning
    return {
      position: 'fixed',
      top: popupPosition.top,
      left: popupPosition.left,
      transform: 'translateX(-50%)',
    };
  };

  // Prevent mousedown from clearing the native text selection
  const handlePopupMouseDown = useCallback((e) => {
    e.preventDefault();
  }, []);

  // Animation direction based on placement
  const animationY = isMobile ? 20 : (popupPosition.placement === 'above' ? 10 : -10);

  const popupContent = (
    <AnimatePresence mode="wait">
      <motion.div
        ref={popupRef}
        className="ask-graspd-popup"
        style={getPopupStyle()}
        initial={{ opacity: 0, y: animationY, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: animationY, scale: 0.95 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        onMouseDown={handlePopupMouseDown}
        role="dialog"
        aria-label="Ask Graspd"
      >
        {/* Initial state - just the button */}
        {isInitial && (
          <motion.button
            className="ask-graspd-trigger"
            onClick={expand}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <span className="ask-graspd-trigger-icon">{Icons.sparkle}</span>
            <span>Ask Graspd</span>
          </motion.button>
        )}

        {/* Expanded state - question type selection */}
        {isExpanded && (
          <motion.div
            className="ask-graspd-card"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.15 }}
          >
            <div className="ask-graspd-header">
              <span className="ask-graspd-header-text">How can I help?</span>
              <button className="ask-graspd-close" onClick={onClose} aria-label="Close">
                ✕
              </button>
            </div>

            {error && (
              <div className="ask-graspd-error" role="alert">
                {error}
              </div>
            )}

            <div className="ask-graspd-options">
              {QUESTION_TYPES.map(({ type, label, icon }) => (
                <motion.button
                  key={type}
                  className="ask-graspd-option"
                  onClick={() => askQuestion(type)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="ask-graspd-option-icon">{icon}</span>
                  <span>{label}</span>
                </motion.button>
              ))}
            </div>

            <button className="ask-graspd-custom-trigger" onClick={showCustomInput}>
              Ask your own question...
            </button>
          </motion.div>
        )}

        {/* Custom question input */}
        {isCustom && (
          <motion.div
            className="ask-graspd-card"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.15 }}
          >
            <div className="ask-graspd-header">
              <button className="ask-graspd-back" onClick={goBack}>
                ← Back
              </button>
              <button className="ask-graspd-close" onClick={onClose} aria-label="Close">
                ✕
              </button>
            </div>

            <textarea
              ref={textareaRef}
              className="ask-graspd-textarea"
              value={customQuestion}
              onChange={(e) => setCustomQuestion(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="What would you like to know about this?"
              rows={3}
              maxLength={500}
            />

            <div className="ask-graspd-custom-footer">
              <span className="ask-graspd-char-count">
                {customQuestion.length}/500
              </span>
              <motion.button
                className="ask-graspd-submit"
                onClick={handleCustomSubmit}
                disabled={customQuestion.trim().length < 5}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Ask
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* Loading state */}
        {isLoading && (
          <motion.div
            className="ask-graspd-card ask-graspd-loading"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.15 }}
            aria-live="polite"
          >
            <div className="ask-graspd-loading-icon">{Icons.sparkle}</div>
            <p className="ask-graspd-loading-text">Graspd is thinking...</p>
            <div className="ask-graspd-loading-dots">
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={i}
                  className="ask-graspd-loading-dot"
                  animate={{ opacity: [0.3, 1, 0.3], scale: [0.8, 1, 0.8] }}
                  transition={{
                    duration: 1.4,
                    repeat: Infinity,
                    delay: i * 0.16,
                  }}
                />
              ))}
            </div>
            <p className="ask-graspd-loading-preview">
              "{selection.text.slice(0, 50)}{selection.text.length > 50 ? '...' : ''}"
            </p>
          </motion.div>
        )}

        {/* Response display */}
        {showResponse && response && (
          <motion.div
            className="ask-graspd-card ask-graspd-response"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.15 }}
            aria-live="polite"
          >
            <div className="ask-graspd-response-header">
              <div className="ask-graspd-response-title">
                <span className="ask-graspd-response-icon">{Icons.sparkle}</span>
                <span>Graspd</span>
              </div>
              <button className="ask-graspd-close" onClick={onClose} aria-label="Close">
                ✕
              </button>
            </div>

            <div className="ask-graspd-response-question">
              You asked: "{QUESTION_LABELS[questionType] || 'Custom question'}"
            </div>

            <div className="ask-graspd-response-content">
              <ReactMarkdown>{response}</ReactMarkdown>
            </div>

            <div className="ask-graspd-response-actions">
              <button className="ask-graspd-action-primary" onClick={reset}>
                Ask follow-up
              </button>
              <button className="ask-graspd-action-secondary" onClick={onClose}>
                Close
              </button>
            </div>
          </motion.div>
        )}
      </motion.div>
    </AnimatePresence>
  );

  return createPortal(popupContent, document.body);
}
